import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {
    fetchWaitingState,
    fetchWaitingInfo,
    setWaitingStatus,
    setGlobalWaitQueue,
    setSubWaitQueue,
    expel,
    setWaitingInfo,
    getQueueInfo,
    setWaitingQueueInfo,
    setExpel,
    getTicketExpelByPolling,
    getQueueInfoByPolling,
    requestEnter,
} from '@web/actions/waiting'
import {WaitingStatus} from '@web/core/constants'
import {getWaitingStatus, isExpiredTicketError, isWaitingEnd, getInitialState} from '@web/slices/waiting/slice'

import type {WaitingReducers, MakeSliceOptions} from './interfaces'
import type {WaitingState} from '@web/slices/waiting/slice'

/** 티켓 요청 최대 재시도 횟수 */
const MAXIMUM_FETCH_TICKET_RETRY_TIMES = 1

export default (options?: MakeSliceOptions) =>
    createSlice<WaitingState, WaitingReducers, 'waiting', SliceSelectors<WaitingState>>({
        name: 'waiting',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(fetchWaitingState.pending, (state) => {
                    state.status = WaitingStatus.TICKET_PENDING
                })
                .addCase(fetchWaitingState.rejected, (state, {payload}) => {
                    // 티켓이 만료되었으나, 재시도 횟수 제한에 도달할 때까지 티켓 정보를 재조회합니다.
                    if (
                        isExpiredTicketError(payload) &&
                        state.fetchTicketRetryTimes < MAXIMUM_FETCH_TICKET_RETRY_TIMES
                    ) {
                        state.fetchTicketRetryTimes += 1
                        state.status = WaitingStatus.EXPIRED
                    } else {
                        // 재시도 횟수 제한에 도달하였으나, 실패하는 경우 그대로 주문서로 진입합니다.
                        state.status = WaitingStatus.ENTERABLE
                    }
                })
                .addCase(fetchWaitingState.fulfilled, (state, {payload}) => {
                    state.status = payload.enterable ? WaitingStatus.ENTERABLE : WaitingStatus.TICKET_ISSUED
                    state.ticketId = payload.ticketId ?? ''

                    if (payload.version) {
                        state.version = payload.version
                        if (payload?.version === 'V2') {
                            state.ticket = payload.ticket
                        }
                    }
                })
                .addCase(requestEnter.pending, (state) => {
                    state.status = WaitingStatus.ENTER_REQUESTING
                })
                .addCase(requestEnter.fulfilled, (state) => {
                    state.status = WaitingStatus.ENTERABLE
                })
                .addCase(fetchWaitingInfo.pending, (state) => {
                    state.status = WaitingStatus.TICKET_CHECK_PENDING
                })
                .addCase(setWaitingStatus, (state, {payload}) => {
                    state.status = payload
                })
                .addCase(setGlobalWaitQueue, (state, {payload}) => {
                    state.globalWaitQueue = payload
                    // 입장가능한 순번에 도달하는 경우, 대기열 정보를 조회합니다.
                    if (!state.enterable && isWaitingEnd(state.globalSubTicket, payload)) {
                        state.status = WaitingStatus.TICKET_ISSUED
                    }
                })
                .addCase(setSubWaitQueue, (state, {payload}) => {
                    const queue = state.subTicketAndWaitQueueList.find(({queueId}) => queueId === payload.queueId)
                    if (queue) {
                        if (queue.waitQueue) {
                            queue.waitQueue.bandwidth = payload.bandwidth
                            queue.waitQueue.lastEnterableWaitNumber = payload.lastEnterableWaitNumber
                            queue.waitQueue.lastWaitNumber = payload.lastWaitNumber
                            queue.waitQueue.queueActive = payload.queueActive
                        } else {
                            queue.waitQueue = {
                                bandwidth: payload.bandwidth,
                                lastEnterableWaitNumber: payload.lastEnterableWaitNumber,
                                lastWaitNumber: payload.lastWaitNumber,
                                queueActive: payload.queueActive,
                            }
                        }
                        // 대기열이 만료된 경우, 대기열 정보를 조회하도록 한다.
                        if (!queue.enterable && isWaitingEnd(queue.subTicket, payload)) {
                            state.status = WaitingStatus.TICKET_ISSUED
                        }
                    }
                })
                .addCase(expel, (state, {payload}) => {
                    state.expelledReason = payload.reason
                    state.status = WaitingStatus.EXPELLED
                })
                .addCase(getQueueInfo.pending, (state) => {
                    state.status = WaitingStatus.QUEUING
                })
                .addCase(setWaitingQueueInfo, (state, {payload}) => {
                    const {position, estimatedWaitSeconds, queueActive, canEnter, message} = payload
                    state.queue = {
                        queueActive,
                        position,
                        estimatedWaitSeconds,
                        canEnter,
                        message,
                    }
                })
                .addCase(setExpel, (state, {payload}) => {
                    state.status = WaitingStatus.EXPELLED
                    state.expelled = true
                    state.expelReason = payload.expelReason
                })
                .addCase(getQueueInfoByPolling.fulfilled, (state, {payload}) => {
                    const {queueActive, canEnter, position, estimatedWaitSeconds, message} = payload
                    state.queue = {
                        ...state.queue,
                        queueActive,
                        position,
                        estimatedWaitSeconds,
                        canEnter,
                        message,
                    }
                })
                .addCase(getTicketExpelByPolling.fulfilled, (state, {payload}) => {
                    if (payload.expelled) {
                        state.status = WaitingStatus.EXPELLED
                        state.expelled = true
                        state.expelReason = payload.expelReason ?? ''
                    }
                })
            options?.extraReducers?.(builder)

            builder
                .addMatcher(isAnyOf(fetchWaitingInfo.fulfilled, setWaitingInfo.fulfilled), (state, {payload}) => {
                    const {enterable, expelled, expired} = payload
                    return {
                        ...state,
                        status: getWaitingStatus([enterable, expelled, expired]),
                        ...payload,
                    }
                })
                .addMatcher(isAnyOf(getQueueInfoByPolling.pending, getTicketExpelByPolling.pending), (state) => {
                    state.status = WaitingStatus.POLLING
                })
        },
    })
